import React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Plane, RefreshCw, ChevronRight, Info, SlidersHorizontal } from 'lucide-react';
import { searchFlights } from '../../services/flightService';
import {
  airportLabel, formatClock, todayStr, CABIN_LABEL, formatMoney,
  describeFareCondition, priceBreakdown,
} from './flightData';
import AirportPicker from './AirportPicker';
import FlightFilters from './FlightFilters';
import SliceRow from './SliceRow';
import {
  DEFAULT_FILTERS, applyFilters, sortOffers, offerSlices, tripLegs, TRIP_TYPES, MAX_LEGS,
} from './flightFilters';

// 往返用「去程/返程」，多程用「第 N 程」
const sliceLabel = (i, total) => {
  if (total === 2) return i === 0 ? '去程' : '返程';
  return `第 ${i + 1} 程`;
};

const usePageTitle = (title) => {
  React.useEffect(() => {
    if (title) document.title = title;
  }, [title]);
};

const OFFER_STORE_KEY = 'flight:selected';
// 选完航班再返回时，结果要还在——重新搜一次既慢（8-9 秒）又要多付一次查询费
const SEARCH_STORE_KEY = 'flight:lastSearch';

const saveSearch = (data) => {
  try {
    sessionStorage.setItem(SEARCH_STORE_KEY, JSON.stringify(data));
  } catch (e) { /* 存不下就算了，不影响功能 */ }
};

const loadSearch = () => {
  try {
    const raw = sessionStorage.getItem(SEARCH_STORE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    // 报价过期就别拿出来了，否则用户选了个已失效的报价，下单才被拒
    if (!data?.expiresAt || new Date(data.expiresAt).getTime() <= Date.now()) return null;
    return data;
  } catch {
    return null;
  }
};

export const getSelectedOffer = () => {
  try {
    return JSON.parse(sessionStorage.getItem(OFFER_STORE_KEY) || 'null');
  } catch {
    return null;
  }
};

export const setSelectedOffer = (data) => {
  sessionStorage.setItem(OFFER_STORE_KEY, JSON.stringify(data));
};

const FlightSearchPage = () => {
  usePageTitle('航班查询 | 旅报报·机票');
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // URL 带了航线参数说明用户刚表达了新意图（多半来自首页的快速查询），
  // 此时必须以参数为准；只有没参数时才恢复上次的搜索结果（用于从下单页返回）
  const hasUrlIntent = !!(params.get('from') && params.get('to'));
  const restored = React.useRef(hasUrlIntent ? null : loadSearch());

  const [criteria, setCriteria] = React.useState(() => restored.current?.criteria || ({
    trip_type: 'ow',
    origin: params.get('from') || 'CAN',
    destination: params.get('to') || 'LAX',
    depart_date: params.get('date') || todayStr(),
    return_date: '',
    // 多程各段；仅 trip_type==='mc' 时使用
    legs: [
      { origin: params.get('from') || 'CAN', destination: params.get('to') || 'LAX', depart_date: params.get('date') || todayStr() },
      { origin: params.get('to') || 'LAX', destination: 'JFK', depart_date: '' },
    ],
    cabin: 'econ',
    adults: 1,
    children: 0,
    infants: 0,
  }));
  const [offers, setOffers] = React.useState(() => restored.current?.offers || []);
  const [expiresAt, setExpiresAt] = React.useState(() => restored.current?.expiresAt || null);
  const [loading, setLoading] = React.useState(false);
  const [searched, setSearched] = React.useState(() => !!restored.current);
  const [error, setError] = React.useState('');
  const [filters, setFilters] = React.useState(DEFAULT_FILTERS);
  const [sortBy, setSortBy] = React.useState('price');

  // 筛选与排序都在客户端做：结果集已完整（含全部直飞），改条件无需重新查询
  const visibleOffers = React.useMemo(
    () => sortOffers(applyFilters(offers, filters), sortBy),
    [offers, filters, sortBy],
  );

  const doSearch = React.useCallback(async (c = criteria) => {
    const legs = tripLegs(c);

    if (c.trip_type === 'rt' && !c.return_date) {
      setError('请选择返程日期'); setSearched(true); setOffers([]); return;
    }
    if (c.trip_type === 'mc' && legs.some((l) => !l.depart_date)) {
      setError('请为每一程选择出发日期'); setSearched(true); setOffers([]); return;
    }

    setLoading(true);
    setError('');
    setOffers([]);
    setFilters(DEFAULT_FILTERS); // 换航线还留着上次的航司筛选会导致莫名空结果
    try {
      const resp = await searchFlights({
        origin: legs[0].origin,
        destination: legs[0].destination,
        depart_date: legs[0].depart_date,
        return_date: c.trip_type === 'rt' ? c.return_date : undefined,
        trip_type: c.trip_type,
        legs: c.trip_type === 'mc' ? legs : undefined,
        cabin: c.cabin,
        adults: Number(c.adults),
        children: Number(c.children),
        infants: Number(c.infants),
      });
      setOffers(resp.offers || []);
      setExpiresAt(resp.expires_at || null);
      setSearched(true);
      saveSearch({ criteria: c, offers: resp.offers || [], expiresAt: resp.expires_at || null });
    } catch (e) {
      setError(e.message || '查询失败，请稍后重试');
      setSearched(true);
    } finally {
      setLoading(false);
    }
  }, [criteria]);

  // 从首页带参进入时自动搜索一次
  // 已从缓存恢复出结果时不要再自动搜一次，否则白白多花一次查询
  const autoSearched = React.useRef(!!restored.current);
  React.useEffect(() => {
    if (autoSearched.current) return;
    if (params.get('from') && params.get('to')) {
      autoSearched.current = true;
      doSearch();
    }
  }, [params, doSearch]);

  const update = (patch) => setCriteria((c) => ({ ...c, ...patch }));

  const updateLeg = (i, patch) => setCriteria((c) => ({
    ...c,
    legs: c.legs.map((l, idx) => (idx === i ? { ...l, ...patch } : l)),
  }));

  // 新增一程默认从上一程的目的地续飞，省得用户重复选
  const addLeg = () => setCriteria((c) => {
    const last = c.legs[c.legs.length - 1];
    return { ...c, legs: [...c.legs, { origin: last.destination, destination: '', depart_date: '' }] };
  });

  const removeLeg = (i) => setCriteria((c) => ({ ...c, legs: c.legs.filter((_, idx) => idx !== i) }));

  const chooseOffer = (offer) => {
    setSelectedOffer({ offer, criteria });
    navigate('/flight/book');
  };

  const inputCls = 'w-full h-11 px-3 border border-gray-200 rounded-xl bg-white text-gray-900 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent';
  const labelCls = 'block text-xs text-gray-500 mb-1';

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-cyan-50 to-white">
      <header className="sticky top-0 z-40 backdrop-blur bg-white/80 border-b border-white/40">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="旅报报 logo" className="w-9 h-9" />
            <div className="font-extrabold text-gray-900">旅报报·机票</div>
          </Link>
          <nav className="flex items-center gap-4 text-sm text-gray-700">
            <Link to="/flight/orders" className="hover:text-gray-900">我的订单</Link>
            <Link
              to="/flight"
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-sky-500 to-cyan-500 text-white font-medium hover:from-sky-600 hover:to-cyan-600 transition-all"
            >
              查询机票
            </Link>
          </nav>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* 搜索栏 */}
        <form
          onSubmit={(e) => { e.preventDefault(); doSearch(); }}
          className="rounded-3xl bg-white/95 border border-sky-100 shadow-lg p-6 mb-6"
        >
          {/* 行程类型 */}
          <div className="flex gap-2 mb-4">
            {TRIP_TYPES.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => update({ trip_type: t.key })}
                className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                  criteria.trip_type === t.key
                    ? 'bg-sky-500 text-white border-sky-500'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-sky-300'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {criteria.trip_type === 'mc' ? (
            <div className="space-y-3 mb-4">
              {criteria.legs.map((leg, i) => (
                <div key={i} className="grid grid-cols-2 md:grid-cols-7 gap-3 items-end">
                  <div className="md:col-span-2">
                    <AirportPicker
                      label={`第 ${i + 1} 程 · 出发地`}
                      value={leg.origin}
                      exclude={leg.destination}
                      onChange={(code) => updateLeg(i, { origin: code })}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <AirportPicker
                      label="目的地"
                      value={leg.destination}
                      exclude={leg.origin}
                      onChange={(code) => updateLeg(i, { destination: code })}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelCls}>出发日期</label>
                    <input
                      type="date"
                      min={i === 0 ? todayStr() : (criteria.legs[i - 1].depart_date || todayStr())}
                      value={leg.depart_date}
                      onChange={(e) => updateLeg(i, { depart_date: e.target.value })}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    {criteria.legs.length > 2 && (
                      <button
                        type="button"
                        onClick={() => removeLeg(i)}
                        className="h-11 px-3 rounded-xl border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 text-sm"
                      >
                        删除
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {criteria.legs.length < MAX_LEGS && (
                <button
                  type="button"
                  onClick={addLeg}
                  className="text-sm text-sky-600 hover:text-sky-700 font-medium"
                >
                  + 添加一程（最多 {MAX_LEGS} 程）
                </button>
              )}
            </div>
          ) : null}

          <div className="grid grid-cols-2 md:grid-cols-7 gap-4 items-end">
            {criteria.trip_type !== 'mc' && (
              <>
                <AirportPicker
                  label="出发地"
                  value={criteria.origin}
                  exclude={criteria.destination}
                  onChange={(code) => update({ origin: code })}
                />
                <AirportPicker
                  label="目的地"
                  value={criteria.destination}
                  exclude={criteria.origin}
                  onChange={(code) => update({ destination: code })}
                />
                <div>
                  <label className={labelCls}>出发日期</label>
                  <input type="date" min={todayStr()} value={criteria.depart_date}
                    onChange={(e) => update({ depart_date: e.target.value })} className={inputCls} />
                </div>
                {criteria.trip_type === 'rt' && (
                  <div>
                    <label className={labelCls}>返程日期</label>
                    <input type="date" min={criteria.depart_date || todayStr()} value={criteria.return_date}
                      onChange={(e) => update({ return_date: e.target.value })} className={inputCls} />
                  </div>
                )}
              </>
            )}
            <div>
              <label className={labelCls}>舱位</label>
              <select value={criteria.cabin} onChange={(e) => update({ cabin: e.target.value })} className={inputCls}>
                <option value="econ">{CABIN_LABEL.econ}</option>
                <option value="business">{CABIN_LABEL.business}</option>
              </select>
            </div>
            {/* 三个下拉挤在一列会把文字裁掉，占两列并各自标注 */}
            <div className="col-span-2">
              <label className={labelCls}>人数</label>
              <div className="grid grid-cols-3 gap-1.5">
                {[
                  { key: 'adults', label: '成人', opts: [1, 2, 3, 4, 5, 6, 7, 8] },
                  { key: 'children', label: '儿童', opts: [0, 1, 2, 3, 4] },
                  { key: 'infants', label: '婴儿', opts: [0, 1, 2] },
                ].map((p) => (
                  <div key={p.key}>
                    <select
                      value={criteria[p.key]}
                      onChange={(e) => update({ [p.key]: e.target.value })}
                      className={inputCls}
                      aria-label={p.label}
                    >
                      {p.opts.map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <p className="text-[11px] text-gray-400 text-center mt-0.5">{p.label}</p>
                  </div>
                ))}
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="h-11 px-6 rounded-xl bg-gradient-to-r from-sky-500 to-cyan-500 text-white font-bold hover:from-sky-600 hover:to-cyan-600 transition-all disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {loading ? '查询中' : '搜索'}
            </button>
          </div>
        </form>

        {error && (
          <div className="mb-6 p-4 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
        )}

        {/* 结果列表 */}
        {searched && !loading && offers.length === 0 && !error && (
          <div className="text-center py-16 text-gray-500">
            <Plane className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            未找到符合条件的航班，请调整日期或航线
          </div>
        )}

        {offers.length > 0 && (
          <>
            <div className="flex items-center gap-2 mb-4 px-1 text-sm text-gray-500">
              <Info className="w-4 h-4 text-sky-500" />
              {tripLegs(criteria).map((l, i) => (
                <span key={i}>
                  {i > 0 && <span className="mx-1 text-gray-300">|</span>}
                  {airportLabel(l.origin)} → {airportLabel(l.destination)} {l.depart_date}
                </span>
              ))}
              ·{' '}
              <span className="text-amber-600 font-medium">
                实时报价{expiresAt ? ` · ${formatClock(expiresAt)} 前有效` : ''}，逾期请重新查询
              </span>
            </div>

            <FlightFilters
              offers={offers}
              filters={filters}
              sortBy={sortBy}
              onFiltersChange={setFilters}
              onSortChange={setSortBy}
              matchedCount={visibleOffers.length}
            />

            {visibleOffers.length === 0 && (
              <div className="text-center py-16 text-gray-500">
                <SlidersHorizontal className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>当前筛选条件下没有航班</p>
                <button
                  type="button"
                  onClick={() => setFilters(DEFAULT_FILTERS)}
                  className="mt-4 px-5 py-2 rounded-xl bg-sky-500 text-white text-sm font-medium hover:bg-sky-600"
                >
                  清除筛选
                </button>
              </div>
            )}

            <div className="space-y-4">
              {visibleOffers.map((offer) => {
                const slices = offerSlices(offer);
                const first = offer.segments[0];
                const multi = slices.length > 1;
                const price = priceBreakdown(offer);
                return (
                  <div key={offer.offer_id} className="rounded-3xl bg-white border border-sky-100 shadow-sm hover:shadow-lg hover:border-sky-300 transition-all duration-300 p-5 md:p-6">
                    <div className="grid md:grid-cols-[1fr_auto] gap-4 items-center">
                      <div className="space-y-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-100">
                            {CABIN_LABEL[first.cabin] || '经济舱'}
                          </span>
                          {[
                            describeFareCondition(offer.refund, 'refund'),
                            describeFareCondition(offer.change, 'change'),
                          ].map((c) => (
                            <span key={c.label} className={`text-xs px-2 py-0.5 rounded-full border ${c.tone}`}>
                              {c.label}
                            </span>
                          ))}
                        </div>
                        {slices.map((sl, i) => (
                          <SliceRow
                            key={i}
                            slice={sl}
                            label={multi ? sliceLabel(i, slices.length) : null}
                            expanded
                          />
                        ))}
                      </div>
                      <div className="md:text-right md:border-l md:border-gray-100 md:pl-6 flex md:flex-col items-center md:items-end justify-between gap-3">
                        <div>
                          <p className="text-3xl font-black text-sky-600">
                            {formatMoney(price.perAdult, price.currency)}
                            <span className="text-sm text-gray-400 font-medium">/成人</span>
                          </p>
                          <p className="text-xs text-gray-400">
                            票价 {formatMoney(price.fare, price.currency)} + 税费 {formatMoney(price.tax, price.currency)}
                          </p>
                          <p className="text-xs text-gray-400">余票 {offer.seats_left} 张</p>
                        </div>
                        <button
                          onClick={() => chooseOffer(offer)}
                          className="inline-flex items-center gap-1 px-6 py-3 rounded-xl bg-gradient-to-r from-sky-500 to-cyan-500 text-white font-bold hover:from-sky-600 hover:to-cyan-600 hover:shadow-lg transition-all"
                        >
                          选择 <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default FlightSearchPage;
